import React from 'react';
import { useTranslation } from 'react-i18next';

// Was missing tracking_link — a previous pass added the {tracking_link} placeholder to
// the "Available placeholders" banner and the real send, but never to this preview's
// sample data, so it silently rendered as the literal text "{tracking_link}" instead of
// resolving like every other token. Sample URL below is representative of the real
// format (tracking.tabliya.nl/t/<prefix+order id>/<token>, see utils/tracking_token.py)
// but is not a real order — purely illustrative, same spirit as the other sample values.
const SAMPLE_CONTEXT = {
    customer_name: 'Jan Jansen',
    order_number: '1234',
    items: '2x Kofta<br/>1x Hawawshi',
    delivery_date: '15-01-2026',
    // Same wording as the real emails (services/delivery_windows.py); sample values only
    delivery_window: 'tussen 09:00 en 12:00 / between 09:00 and 12:00',
    expected_delivery: 'Verwachte bezorging: donderdag 15 januari 2026, tussen 09:00 en 12:00 / Expected delivery: Thursday, 15 January 2026, between 09:00 and 12:00',
    delivered_at: 'Bezorgd op dinsdag 13 oktober 2026 om 14:32 / Delivered on Tuesday, 13 October 2026 at 14:32',
    tracking_link: 'https://tracking.tabliya.nl/t/TAB001234/a1B2c3D4e5F6',
};

function substitutePlaceholders(text) {
    let result = text || '';
    for (const [key, val] of Object.entries(SAMPLE_CONTEXT)) {
        result = result.split(`{${key}}`).join(val);
    }
    return result;
}

// Mirrors the backend's build_branded_html() conditional logic exactly (see
// utils/email_sender.py) so this preview reflects what will actually be sent, not a
// divergent approximation. Uses data URIs directly (fine for an in-browser preview —
// the real send uses CID-embedded images for email-client compatibility instead).
export default function EmailPreview({ subject, body, includeLogo, attachInvoicePdf, branding }) {
    const { t } = useTranslation();
    const substitutedSubject = substitutePlaceholders(subject);
    const substitutedBody = substitutePlaceholders(body);
    const hasLogo = includeLogo && branding?.logo_base64;

    return (
        <div className="email-preview-pane border rounded bg-white h-100">
            <div className="border-bottom px-3 py-2 bg-light">
                <small className="text-muted d-block">{t('emailTemplates.preview.subjectLabel')}</small>
                <strong>{substitutedSubject || <span className="text-muted fst-italic">{t('emailTemplates.preview.emptySubject')}</span>}</strong>
            </div>
            <div className="p-3" style={{ maxHeight: 500, overflowY: 'auto' }}>
                {hasLogo && (
                    <div className="text-center mb-3">
                        <img src={branding.logo_base64} alt="" style={{ maxHeight: 80, maxWidth: 300 }} />
                    </div>
                )}
                {substitutedBody ? (
                    <div dangerouslySetInnerHTML={{ __html: substitutedBody }} />
                ) : (
                    <p className="text-muted fst-italic">{t('emailTemplates.preview.emptyBody')}</p>
                )}
            </div>
            {attachInvoicePdf && (
                <div className="border-top px-3 py-2 bg-light small text-muted">
                    <i className="fa-solid fa-paperclip me-1" /> {t('emailTemplates.preview.pdfAttached')}
                </div>
            )}
        </div>
    );
}
